import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import * as Models from '../../../models/report-view.models';
import { SeriesOptionsType } from 'highcharts';
import { fixedPlacementHorizontalBarPlaceholder } from '../../../constants/highcharts-placeholders';
import { ChartService } from '../../chart/chart.service';
import { map, Subject, takeUntil } from 'rxjs';

// Styling / layout constants

const DEFAULT_PRIMARY_COLOR = '#1E3A8A';
const DEFAULT_BACKGROUND_COLOR = '#d0d0d0';
const BAR_WIDTH_BACKGROUND = 24; // px
const BAR_WIDTH_FOREGROUND = 12; // px
const ROW_GAP_PX = 8;            // px gap between category rows
const MIN_HEIGHT_DEFAULT = 400;  // px minimum chart height
const EXTRA_HEIGHT_PADDING = 80; // px padding for axes/legend

interface SimplePoint { y: number; label?: string; }

/**
 * FixedPlacementHorizontalBarComponent
 * Visualizes a single primary metric for two scenarios across a shared dimension using a horizontal fixed-width bar chart.
 * Settings (either top-level or within settings.options):
 *  - dimensionNames: string[] (scenario column first, category column second)
 *  - primarySeriesName / comparativeSeriesName: string (optional legend label overrides)
 *  - chartColors: [string, string] (custom colors for the two series)
 */
@Component({
  selector: 'fixed-placement-horizontal-bar',
  templateUrl: './fixed-placement-horizontal-bar.component.html',
  styleUrls: ['../panel-elements.scss', './fixed-placement-horizontal-bar.component.scss'],
  standalone: false
})
export class FixedPlacementHorizontalBarComponent implements Models.ElementComponent, OnInit, OnDestroy {
  @Input() set dataSets(value: Models.DataSet[]) {
    this.currentDataSet = value?.[0];
    this.rebuildChart();
  }
  @Input() set selectedMetric(value: string | undefined) {
    if (this._selectedMetric === value) {
      return;
    }
    this._selectedMetric = value;
    if (this.currentDataSet) {
      this.rebuildChart();
    }
  }
  @Input() settings: Models.ElementSettings;

  isDataSetLoaded: boolean = false;
  chart: Chart;
  placeholderChart: Chart;
  currentDataSet?: Models.DataSet;
  seriesOneLabel: string;
  seriesTwoLabel: string;
  private _selectedMetric?: string;
  private destroy$ = new Subject<void>();

  constructor(private chartService: ChartService) { }

  ngOnInit(): void {
    this.initializePlaceholder();
    this.chartService.reflowChart$.pipe(
          takeUntil(this.destroy$),
          map(() => {
            setTimeout(() => {
              if (this.chart && this.chart.ref) {
                this.chart.ref.reflow();
              }
            }, 200);
          })
          ).subscribe();
        }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private processData(dataSet: Models.DataSet) {
    if (!dataSet?.rows?.length) {
      throw new Error('[FixedPlacementHorizontalBar] Dataset is empty.');
    }

    const { scenarioIndex, categoryIndex } = this.resolveDimensionColumnIndices(dataSet);
    const availableMetricNames = this.getMetricNames(dataSet);
    const metricsToUse = this.getMetricsFromSettings(availableMetricNames);
    const metricName = this.resolvePrimaryMetricName(metricsToUse);
    const metricIndex = this.getColumnIndex(dataSet, metricName);

    const scenarioOrder: string[] = [];
    const scenarioLabels = new Map<string, string>();
    const scenarioCategoryValues = new Map<string, Map<string, SimplePoint>>();

    const categories: { key: string; label: string }[] = [];
    const categoryIndexMap = new Map<string, number>();

    dataSet.rows.forEach(row => {
      const scenarioKey = this.extractDimensionKey(row, scenarioIndex);
      if (!scenarioKey) {
        return;
      }

      if (!scenarioOrder.includes(scenarioKey)) {
        scenarioOrder.push(scenarioKey);
        scenarioLabels.set(scenarioKey, this.extractDimensionLabel(row, scenarioIndex) || scenarioKey);
      }

      const categoryKey = this.extractDimensionKey(row, categoryIndex);
      const categoryLabel = this.extractDimensionLabel(row, categoryIndex);
      if (!categoryIndexMap.has(categoryKey)) {
        categoryIndexMap.set(categoryKey, categories.length);
        categories.push({ key: categoryKey, label: categoryLabel });
      }

      const point = this.buildPoint(row, metricIndex);
      const categoryMap = scenarioCategoryValues.get(scenarioKey) ?? new Map<string, SimplePoint>();
      categoryMap.set(categoryKey, point);
      scenarioCategoryValues.set(scenarioKey, categoryMap);
    });

    if (scenarioOrder.length < 2) {
      throw new Error('[FixedPlacementHorizontalBar] Expected at least two scenario values to build comparison.');
    }

    const primaryScenarioKey = scenarioOrder[0];
    const comparativeScenarioKey = scenarioOrder[1];

    const primarySeriesData = this.buildSeriesData(categories, scenarioCategoryValues.get(primaryScenarioKey));
    const comparativeSeriesData = this.buildSeriesData(categories, scenarioCategoryValues.get(comparativeScenarioKey));

    this.seriesOneLabel = this.resolveSeriesName('primarySeriesName', scenarioLabels.get(primaryScenarioKey) ?? primaryScenarioKey);
    this.seriesTwoLabel = this.resolveSeriesName('comparativeSeriesName', scenarioLabels.get(comparativeScenarioKey) ?? comparativeScenarioKey);

    const primaryColor = this.settings?.chartColors?.[0] || DEFAULT_PRIMARY_COLOR;
    const comparativeColor = this.settings?.chartColors?.[1] || DEFAULT_BACKGROUND_COLOR;

    const comparativeSeries = this.buildSeriesOptions(
      this.seriesTwoLabel,
      comparativeSeriesData,
      comparativeColor,
      BAR_WIDTH_BACKGROUND,
      1,
      false
    );

    const primarySeries = this.buildSeriesOptions(
      this.seriesOneLabel,
      primarySeriesData,
      primaryColor,
      BAR_WIDTH_FOREGROUND,
      2,
      true
    );

    const chartCategories = categories.map(c => c.label);
    const dynamicHeight = this.computeDynamicHeight(chartCategories.length);
    this.chart = this.buildChart(chartCategories, primarySeries, comparativeSeries, dynamicHeight);
  }

  private rebuildChart(): void {
    if (!this.currentDataSet) {
      this.isDataSetLoaded = false;
      this.chart = undefined;
      return;
    }

    try {
      this.processData(this.currentDataSet);
      this.isDataSetLoaded = true;
    } catch (error) {
      this.isDataSetLoaded = false;
      this.chart = undefined;
      throw error;
    }
  }

  private getMetricNames(dataSet: Models.DataSet): string[] {
    const metricNames = dataSet.columns.filter(column => column.type === 'metric').map(column => column.name);
    if (!metricNames.length) {
      throw new Error('[FixedPlacementHorizontalBar] Dataset does not contain any metric columns.');
    }

    return metricNames;
  }

  private getMetricsFromSettings(availableMetricNames: string[]): string[] {
    const configuredMetrics = this.settings?.metrics ?? [];
    if (!configuredMetrics.length) {
      return availableMetricNames;
    }

    const configuredNames = configuredMetrics
      .map(metric => metric?.name)
      .filter((name): name is string => typeof name === 'string' && name.length > 0);

    const matchingNames = availableMetricNames.filter(name => configuredNames.includes(name));
    if (!matchingNames.length) {
      throw new Error('[FixedPlacementHorizontalBar] None of the configured metrics are available in the dataset.');
    }

    return matchingNames;
  }

  private resolvePrimaryMetricName(availableMetricNames: string[]): string {
    if (this._selectedMetric && availableMetricNames.includes(this._selectedMetric)) {
      return this._selectedMetric;
    }

    const configuredMetric = this.settings?.metricName;
    if (configuredMetric && availableMetricNames.includes(configuredMetric)) {
      return configuredMetric;
    }

    const chartMetrics = this.settings?.chartMetrics ?? [];
    const matchingChartMetric = chartMetrics.find(metric => availableMetricNames.includes(metric));
    if (matchingChartMetric) {
      return matchingChartMetric;
    }

    return availableMetricNames[0];
  }

  private resolveDimensionColumnIndices(dataSet: Models.DataSet): { scenarioIndex: number; categoryIndex: number } {
    const dimensionNames = this.settings?.dimensionNames ?? [];
    const [scenarioName, categoryName] = dimensionNames;
    if (!scenarioName || !categoryName) {
      throw new Error('[FixedPlacementHorizontalBar] ElementSettings.dimensionNames must include scenario and category columns.');
    }

    const scenarioIndex = this.getColumnIndex(dataSet, scenarioName);
    const categoryIndex = this.getColumnIndex(dataSet, categoryName);

    return { scenarioIndex, categoryIndex };
  }

  private getColumnIndex(dataSet: Models.DataSet, columnName: string): number {
    const index = dataSet.columns.findIndex(column => column.name === columnName);
    if (index === -1) {
      throw new Error(`[FixedPlacementHorizontalBar] Column "${columnName}" was not found in dataset "${dataSet.name}".`);
    }

    return index;
  }

  private extractDimensionLabel(row: Models.RowValue[], dimensionIndex: number): string {
    const cell = row[dimensionIndex];
    return cell?.label ?? (cell?.value !== undefined ? String(cell.value) : '');
  }

  private extractDimensionKey(row: Models.RowValue[], dimensionIndex: number): string {
    const cell = row[dimensionIndex];
    if (!cell) {
      return '';
    }

    if (cell.value !== undefined && cell.value !== null && cell.value !== '') {
      return String(cell.value);
    }

    return cell.label ?? '';
  }

  private buildPoint(row: Models.RowValue[], columnIndex: number): SimplePoint {
    const cell = row[columnIndex];
    if (!cell) {
      return { y: 0 };
    }

    const rawValue = cell.value;
    const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
    const y = Number.isFinite(numericValue) ? numericValue : 0;
    return cell.label ? { y, label: cell.label } : { y };
  }

  private buildSeriesData(
    categories: { key: string; label: string }[],
    categoryMap?: Map<string, SimplePoint>
  ): SimplePoint[] {
    return categories.map(category => categoryMap?.get(category.key) ?? { y: 0 });
  }

  private resolveSeriesName(optionKey: string, fallback: string): string {
    const optionValue = this.getOption(optionKey);
    if (typeof optionValue === 'string') {
      const trimmed = optionValue.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }

    return fallback;
  }

  private getOption(key: string): string | undefined {
    return this.settings?.options?.[key] ?? undefined;
  }

  private buildSeriesOptions(name: string, data: SimplePoint[], color: string, pointWidth: number, zIndex: number, showDataLabels: boolean): SeriesOptionsType {
    return {
      type: 'bar',
      name,
      data: data as any,
      color,
      pointWidth,
      borderWidth: 0,
      zIndex,
      dataLabels: showDataLabels ? {
        enabled: true,
        inside: false,
        formatter: function () {
          // @ts-ignore Highcharts context
          const pointLabel = this.point?.label;
          const value = this.y ?? '';
          return pointLabel ?? value.toString();
        }
      } : { enabled: false }
    } as SeriesOptionsType;
  }

  private computeDynamicHeight(categoryCount: number): number {
    const minHeightOpt = Number(this.getOption('minHeight')) || MIN_HEIGHT_DEFAULT;
    const perCategoryHeight = BAR_WIDTH_BACKGROUND + ROW_GAP_PX; // simple linear mapping
    return Math.max(minHeightOpt, categoryCount * perCategoryHeight + EXTRA_HEIGHT_PADDING);
  }

  private buildChart(categories: string[], primarySeries: SeriesOptionsType, comparativeSeries: SeriesOptionsType, height: number): Chart {
    return new Chart({
      credits: { enabled: false },
      chart: { type: 'bar', height },
      title: { text: this.settings?.title || '' },
      legend: {
        align: 'left',
        verticalAlign: 'top',
        layout: 'horizontal',
        floating: false,
        y: 0,
        x: 0,
        itemMarginTop: 2,
        itemMarginBottom: 2
      },
      xAxis: { categories },
      yAxis: {
        title: { text: null },
        allowDecimals: false,
        labels: {
          formatter: function () {
            return (this.value as number).toFixed(0);
          }
        }
      },
      tooltip: {
        shared: true,
        formatter: function () {
          // @ts-ignore Highcharts runtime context
          const points = this.points;
          // @ts-ignore
          let s = `<b>${this.x}</b><br/>`;
          points.forEach(p => {
            // @ts-ignore
            const pointLabel = p.point?.label;
            const rawValue = p.point?.y ?? '';
            const displayValue = pointLabel ?? rawValue;
            s += `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${displayValue}</b><br/>`;
          });
          return s;
        }
      },
      plotOptions: {
        series: <any>{
          grouping: false,
          animation: false,
          pointPadding: 0,
          groupPadding: ROW_GAP_PX / (BAR_WIDTH_BACKGROUND + ROW_GAP_PX), // fractional gap
          borderWidth: 0
        }
      },
      // Primary first (desired legend order)
      series: [primarySeries, comparativeSeries]
    });
  }

  private initializePlaceholder(): void {
    if (this.placeholderChart) return;
    setTimeout(() => {
      this.placeholderChart = new Chart(fixedPlacementHorizontalBarPlaceholder);
    }, 0);
  }
}
